// src/components/CheckoutButton.jsx
import React, { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";

/**
 * CheckoutButton
 *
 * Props:
 *  - priceId (string) required: the Stripe Price ID or a backend plan identifier
 *  - quantity (number) optional: default 1
 *  - children: label or button content
 *  - className: optional extra classes for button
 *  - onStart: optional callback invoked when checkout starts
 *  - onError: optional callback invoked with (errorMessage)
 *
 * The backend endpoint expected:
 * POST ${REACT_APP_API_BASE}/create-checkout-session
 * body: { priceId, quantity }
 * returns either { sessionId } or { url }
 */
const CheckoutButton = ({
  priceId,
  quantity = 1,
  children,
  className = "",
  onStart,
  onError
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  const apiBase = process.env.REACT_APP_API_BASE || "";

  const stripePromise = publishableKey ? loadStripe(publishableKey) : null;

  const handleError = (msg) => {
    setError(msg);
    setLoading(false);
    if (onError) onError(msg);
  };

  const handleClick = async () => {
    setError(null);

    if (!priceId) {
      handleError("This plan cannot be purchased from the app. Please contact sales.");
      return;
    }

    // optional callback
    if (onStart) onStart();

    setLoading(true);

    try {
      const resp = await fetch(`${apiBase}/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ priceId, quantity })
      });

      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || `Server returned ${resp.status}`);
      }

      const data = await resp.json();

      // If backend returned a hosted url
      if (data.url) {
        window.location.assign(data.url);
        return;
      }

      // If backend returned sessionId, use Stripe.js to redirect
      if (data.sessionId) {
        if (!stripePromise) {
          throw new Error("Stripe publishable key is not configured (REACT_APP_STRIPE_PUBLISHABLE_KEY).");
        }
        const stripe = await stripePromise;
        const { error } = await stripe.redirectToCheckout({ sessionId: data.sessionId });
        if (error) throw error;
        return;
      }

      throw new Error("Unexpected response from server.");
    } catch (err) {
      console.error("Checkout error:", err);
      handleError(err.message || "Checkout failed. Please try again.");
    }
  };

  return (
    <div>
      <button
        onClick={handleClick}
        disabled={loading}
        className={`w-full py-3 rounded-lg font-medium transition-colors ${loading ? "opacity-60 cursor-wait" : "bg-primary-600 hover:bg-primary-700 text-white" } ${className}`}
      >
        {loading ? "Processing..." : (children || "Buy now")}
      </button>

      {error && (
        <p className="mt-2 text-sm text-red-400">{error}</p>
      )}
    </div>
  );
};

export default CheckoutButton;